/**
 * Segment management screen — exact visual parity with frontend's Segments.Table.
 * Table view with grid/list toggle, create modal, manage button per row.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ArrowLeft, BookOpen, Download, FileText, Layers, Archive, ChevronDown } from 'react-feather';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { People, GridView, ViewList, Edit, ChevronRight, ExpandMore } from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CircularProgress from '@mui/material/CircularProgress';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthenticatedClient } from '@/api/client';
import {
  getSegmentPages,
  createSegmentRecord,
  listSegmentationRuns,
  listSegments,
  downloadSegmentedExcel,
  downloadSegmentedPowerpoint,
  downloadSegmentedAllZip,
} from '@/screens/Segmentation/api/client';
import { getSegmentAvatar } from '@/screens/Segmentation/constants/avatars';
import { buildBackLinkState } from '@/screens/Segmentation/utils/backLink';
import { buildSegmentDashboard } from '@trinity/chat/utils/build-segment-dashboard';
import { SEGMENT_CARD_COLORS } from '@trinity/chat/utils/segment-constants';

// Exported so the project page can prefetch segments before the user
// clicks into the Segments tab — eliminates the load lag.
export const SEGMENTS_QUERY_KEY = (projectId: number | undefined) =>
  ['segments-list', projectId ?? 'all'] as const;

export async function fetchSegmentsForProject(projectId?: number, view?: 'list'): Promise<Segment[]> {
  const res = await getAuthenticatedClient().get('/segments', {
    params: {
      ...(projectId != null ? { projectId } : {}),
      ...(view ? { view } : {}),
    },
  });
  return (res.data.segments ?? []) as Segment[];
}

interface Segment {
  id: number;
  identifier: string;
  projectId: number;
  name: string;
  description: string | null;
  metadata: Record<string, unknown>;
  dashboardPageId: number | null;
  iconUrl: string | null;
  isPublished: boolean;
  createdOn: string;
  updatedOn: string;
}

interface Run {
  runId: string;
  title: string;
  timestamp: string;
  sessionId: string | null;
  segments: Segment[];
}

function client() {
  return getAuthenticatedClient();
}

// ─── Main Component ─────────────────────────────────────────────────────────

export type SegmentListScreenProps = {
  /** When set, filter segments to this project id only. */
  projectId?: number;
  /** Navigation base for segment click-through. Defaults to `/segments`.
   *  Pass `/projects/11/segments` to keep the user inside a project subtab. */
  navBase?: string;
  /** Skip the outer card shell (Root + Card + Divider) so the list fits
   *  cleanly inside an already-chrome'd surface like a project tab. */
  embedded?: boolean;
  /** Use skinny list view (2-5KB per segment) instead of fat view (20-80KB with quotes/qual).
   *  Significantly improves load time for large segment lists. */
  useSkinnyView?: boolean;
  /** Hide the Manage button in table/grid views */
  hideManage?: boolean;
  /** Optional back link rendered inside the card header. Callers pass the
   *  resolved { label, path } so the header style stays consistent with
   *  other screens (CrossSegmentDashboardScreen, MessageTesting, etc.). */
  backLink?: { label: string; path: string };
};

export function SegmentListScreen({
  projectId,
  navBase = '/segments',
  embedded = false,
  useSkinnyView = false,
  hideManage = false,
  backLink,
}: SegmentListScreenProps = {}): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [legacySegments, setLegacySegments] = useState<Segment[]>([]);
  const [legacyLoading, setLegacyLoading] = useState(projectId == null);
  const [lazyLoadedRuns, setLazyLoadedRuns] = useState<Run[]>([]);
  const [lazyLoading, setLazyLoading] = useState(false);
  const [runsTotal, setRunsTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const RUNS_PAGE_SIZE = 5;

  // Always use lazy loaded segments since react-query is disabled
  const segments = legacySegments;
  const loading = legacyLoading || lazyLoading;
  const [showCreate, setShowCreate] = useState(false);
  const [layout, setLayout] = useState<'list' | 'grid'>('grid');
  const [segTab, setSegTab] = useState<'auto' | 'manual'>('auto');

  // Lazy load segments by fetching runs first, then segments per run.
  // Supports appending additional pages of runs via the skip parameter.
  const fetchSegmentsLazy = useCallback(
    async (skip = 0) => {
      const isInitial = skip === 0;
      if (isInitial) {
        setLazyLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        // Step 1: Fetch a page of runs (server-sorted by createdOn DESC)
        const runsResponse = await listSegmentationRuns({ status: 'complete', take: RUNS_PAGE_SIZE, skip });
        setRunsTotal(runsResponse.total);
        const fetchedRuns = runsResponse.runs.filter(
          (r) => r.sessionId && r.segmentCount && r.segmentCount > 0
        );

        console.log(
          '[SegmentList] Fetched runs:',
          fetchedRuns.length,
          'skip:',
          skip,
          'total:',
          runsResponse.total
        );

        // Step 2: Fetch segments for runs in parallel
        const runResults = await Promise.allSettled(
          fetchedRuns.map(async (run) => {
            const response = await listSegments({ runId: run.runKey, view: 'list' });
            return {
              runId: run.runKey,
              title: run.projectTitle || `Run ${run.runKey}`,
              timestamp: run.runTimestamp || new Date().toISOString(),
              sessionId: run.sessionId,
              segments: response.segments as unknown as Segment[],
            };
          })
        );

        const newRuns: Run[] = [];
        runResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            newRuns.push(result.value);
          }
        });

        console.log('[SegmentList] Loaded runs with segments:', newRuns.length);

        if (isInitial) {
          setLazyLoadedRuns(newRuns);
          const allSegments: Segment[] = [];
          newRuns.forEach((run) => allSegments.push(...run.segments));
          setLegacySegments(allSegments);
        } else {
          setLazyLoadedRuns((prev) => [...prev, ...newRuns]);
          setLegacySegments((prev) => {
            const additional: Segment[] = [];
            newRuns.forEach((run) => additional.push(...run.segments));
            return [...prev, ...additional];
          });
        }
      } catch (error) {
        console.error('[SegmentList] Failed to lazy load segments:', error);
      } finally {
        setLazyLoading(false);
        setLegacyLoading(false);
        setLoadingMore(false);
      }
    },
    [RUNS_PAGE_SIZE]
  );

  const hasMoreRuns = lazyLoadedRuns.length < runsTotal;

  const handleLoadMoreRuns = useCallback(() => {
    if (loadingMore || !hasMoreRuns) return;
    fetchSegmentsLazy(lazyLoadedRuns.length);
  }, [fetchSegmentsLazy, lazyLoadedRuns.length, loadingMore, hasMoreRuns]);

  // IntersectionObserver-driven infinite scroll. Callback-ref so the observer
  // rebinds whenever the sentinel node attaches/detaches — a plain useRef +
  // useEffect can race with HMR remounts and end up observing nothing.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const handleLoadMoreRunsRef = useRef(handleLoadMoreRuns);
  handleLoadMoreRunsRef.current = handleLoadMoreRuns;
  const loadMoreSentinelRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          handleLoadMoreRunsRef.current();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(el);
    observerRef.current = observer;
  }, []);
  useEffect(() => () => observerRef.current?.disconnect(), []);

  // page-backed segments into the DB on first load.
  const fetchSegments = useCallback(() => {
    if (projectId != null) return; // happy path uses react-query above
    setLegacyLoading(true);
    client()
      .get('/segments')
      .then(async (res) => {
        const dbSegs = res.data.segments ?? [];
        if (dbSegs.length > 0) {
          setLegacySegments(dbSegs);
          return;
        }
        // No DB segments — auto-migrate from legacy pages (global route only)
        try {
          const { pages } = await getSegmentPages();
          const segPages = pages.filter((p) => p.providerData?.segmentProfile);
          if (segPages.length === 0) return;
          let migrated = 0;
          for (const page of segPages) {
            const seg = page.providerData?.segmentProfile as Record<string, unknown> | undefined;
            if (!seg || !seg._runId) continue;
            try {
              const config = buildSegmentDashboard(
                seg as never,
                (seg._dataSourceName as string) || 'Segment Insights'
              );
              await createSegmentRecord({
                projectId: 1,
                name: page.name || `Segment ${page.id}`,
                description: ((seg.profile_narrative as string) ?? '').slice(0, 500) || undefined,
                iconUrl: getSegmentAvatar(migrated),
                ttsSettings: { avatarId: undefined, ttsConfig: null },
                createdOn: page.createdOn,
                metadata: {
                  _hackathon: true,
                  _migratedFromPageId: page.id,
                  _runId: seg._runId,
                  _runTimestamp: seg._runTimestamp,
                  _sessionId: seg._sessionId,
                  _projectTitle: seg._projectTitle,
                  _projectBrand: seg._projectBrand,
                  _dataSourceName: seg._dataSourceName,
                  segmentProfile: seg,
                  subtitle: `${((seg.size as number) ?? 0).toLocaleString()} HCPs (${(((seg.pct as number) ?? 0) * 100).toFixed(0)}%)`,
                  attributes: seg.archetype_distribution
                    ? Object.entries(seg.archetype_distribution as Record<string, number>)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 2)
                        .map(([k]) => k)
                    : undefined,
                  sampleData: {
                    segmentN: (seg.size as number) ?? 0,
                    totalN: Math.round(((seg.size as number) ?? 0) / ((seg.pct as number) || 1)),
                  },
                },
                dashboardConfig: config as unknown as Record<string, unknown>,
              });
              migrated++;
            } catch {
              /* skip */
            }
          }
          // Re-fetch after auto-migration
          if (migrated > 0) {
            const res2 = await client().get('/segments');
            setLegacySegments(res2.data.segments ?? []);
          }
        } catch {
          /* legacy pages not available */
        }
      })
      .catch(() => {})
      .finally(() => setLegacyLoading(false));
  }, [projectId]);

  useEffect(() => {
    // Always use lazy loading approach for better performance
    fetchSegmentsLazy();
  }, [fetchSegmentsLazy]);

  useEffect(() => {
    try {
      localStorage.setItem('segments-layout', layout);
    } catch {
      /**/
    }
  }, [layout]);

  const handleCreated = useCallback(
    (seg: Segment) => {
      setShowCreate(false);
      if (projectId != null) {
        // Invalidate the cached query so the new segment appears on refetch
        void queryClient.invalidateQueries({ queryKey: SEGMENTS_QUERY_KEY(projectId) });
      } else {
        setLegacySegments((prev) => [seg, ...prev]);
      }
      // Use dashboardPageId for navigation if available (for /segmentation/segment routes)
      // Otherwise use v2 id (for /projects/:id/segments routes)
      const targetId = seg.dashboardPageId ?? seg.id;
      navigate(`${navBase}/${targetId}`, { state: buildBackLinkState(location.pathname) });
    },
    [navigate, navBase, projectId, queryClient, location.pathname]
  );

  const handleManage = useCallback(
    (segId: number) => {
      // Manage always uses /segments/:v2Id/manage, regardless of navBase
      navigate(`/segments/${segId}/manage`);
    },
    [navigate]
  );

  // Split segments by auto-generated vs manual
  const autoSegments = useMemo(
    () =>
      segments.filter((s) => {
        const meta = (s.metadata ?? {}) as Record<string, unknown>;
        const sp = (meta.segmentProfile ?? {}) as Record<string, unknown>;
        return !!(meta._runId ?? sp._runId);
      }),
    [segments]
  );

  const manualSegments = useMemo(
    () =>
      segments.filter((s) => {
        const meta = (s.metadata ?? {}) as Record<string, unknown>;
        const sp = (meta.segmentProfile ?? {}) as Record<string, unknown>;
        return !(meta._runId ?? sp._runId);
      }),
    [segments]
  );

  // Show all segments when tabs are hidden
  const filteredSegments = segments; // segTab === 'auto' ? autoSegments : manualSegments;

  // Group segments by run - use lazy loaded runs if available, otherwise compute from segments
  const runs = useMemo(() => {
    if (lazyLoadedRuns.length > 0) {
      return lazyLoadedRuns;
    }

    const runMap = new Map<string, Run>();
    for (const seg of segments) {
      const meta = (seg.metadata ?? {}) as Record<string, unknown>;
      const sp = (meta.segmentProfile ?? {}) as Record<string, unknown>;
      const runId = (meta._runId ?? sp._runId ?? `solo-${seg.id}`) as string;
      const title = (meta._projectTitle ?? sp._projectTitle ?? 'Untitled') as string;
      const timestamp = (meta._runTimestamp ?? sp._runTimestamp ?? seg.createdOn) as string;
      const sessionId = (meta._sessionId ?? sp._sessionId ?? null) as string | null;
      const existing = runMap.get(runId);
      if (existing) {
        existing.segments.push(seg);
      } else {
        runMap.set(runId, { runId, title, timestamp, sessionId, segments: [seg] });
      }
    }
    return Array.from(runMap.values()).sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [segments, lazyLoadedRuns]);

  const [migrating, setMigrating] = useState(false);
  const handleMigrateAll = useCallback(async () => {
    setMigrating(true);
    let migrated = 0;
    let skipped = 0;
    try {
      const { pages } = await getSegmentPages();
      const segPages = pages.filter((p) => p.providerData?.segmentProfile);
      for (const page of segPages) {
        const seg = page.providerData?.segmentProfile as Record<string, unknown> | undefined;
        if (!seg || !seg._runId) {
          skipped++;
          continue;
        }
        try {
          const config = buildSegmentDashboard(
            seg as never,
            (seg._dataSourceName as string) || 'Segment Insights'
          );
          await createSegmentRecord({
            projectId: 1,
            name: page.name || `Segment ${page.id}`,
            description: ((seg.profile_narrative as string) ?? '').slice(0, 500) || undefined,
            iconUrl: getSegmentAvatar(migrated),
            ttsSettings: { avatarId: undefined, ttsConfig: null },
            createdOn: page.createdOn,
            metadata: {
              _hackathon: true,
              _migratedFromPageId: page.id,
              _runId: seg._runId,
              _runTimestamp: seg._runTimestamp,
              _sessionId: seg._sessionId,
              _projectTitle: seg._projectTitle,
              _projectBrand: seg._projectBrand,
              _dataSourceName: seg._dataSourceName,
              segmentProfile: seg,
              subtitle: `${((seg.size as number) ?? 0).toLocaleString()} HCPs (${(((seg.pct as number) ?? 0) * 100).toFixed(0)}%)`,
              attributes: seg.archetype_distribution
                ? Object.entries(seg.archetype_distribution as Record<string, number>)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 2)
                    .map(([k]) => k)
                : undefined,
              sampleData: {
                segmentN: (seg.size as number) ?? 0,
                totalN: Math.round(((seg.size as number) ?? 0) / ((seg.pct as number) || 1)),
              },
            },
            dashboardConfig: config as unknown as Record<string, unknown>,
          });
          migrated++;
        } catch {
          skipped++;
        }
      }
      alert(`Migrated ${migrated}, skipped ${skipped}.`);
      fetchSegments();
    } finally {
      setMigrating(false);
    }
  }, [fetchSegments]);

  const content = (
    <>
      {/* ── Header ── */}
      <CardHeader $compact={useSkinnyView}>
        <HeaderLeft>
          <HeaderTitleBlock>
            {backLink && (
              <HeaderBackLink onClick={() => navigate(backLink.path)}>
                <ArrowLeft size={14} /> {backLink.label}
              </HeaderBackLink>
            )}
            <HeaderTitle>
              <People sx={{ fontSize: 22 }} />
              Explore Segments
            </HeaderTitle>
          </HeaderTitleBlock>
        </HeaderLeft>
        <HeaderActions>
          {/* <SegTabToggle>
            <SegTabBtn $active={segTab === 'auto'} onClick={() => setSegTab('auto')}>
              Auto-Generated ({autoSegments.length})
            </SegTabBtn>
            <SegTabBtn $active={segTab === 'manual'} onClick={() => setSegTab('manual')}>
              Manual ({manualSegments.length})
            </SegTabBtn>
          </SegTabToggle> */}
          <LayoutToggle>
            <LayoutBtn $active={layout === 'list'} onClick={() => setLayout('list')} title="List view">
              <ViewList sx={{ fontSize: 18 }} />
            </LayoutBtn>
            <LayoutBtn $active={layout === 'grid'} onClick={() => setLayout('grid')} title="Grid view">
              <GridView sx={{ fontSize: 18 }} />
            </LayoutBtn>
          </LayoutToggle>
          {/* Create segment button hidden for now — new segments come from
              the segmentation builder, not manual creation. */}
          {/* <CreateBtn onClick={() => setShowCreate(true)}>Create Segment</CreateBtn> */}
        </HeaderActions>
      </CardHeader>

      {/* ── Content ── */}
      <CardContent>
        {loading && <EmptyState>Loading segments...</EmptyState>}

        {!loading && segments.length === 0 && (
          <EmptyState>
            <People sx={{ fontSize: 48, color: 'var(--gray, #adadad)', mb: 1 }} />
            <div>No segments have been created yet.</div>
            {/* <div style={{ marginTop: 8 }}>
                <CreateBtn onClick={() => setShowCreate(true)}>Create Segment</CreateBtn>
              </div> */}
          </EmptyState>
        )}

        {!loading && segments.length > 0 && layout === 'list' && (
          <SegmentTableList
            segments={filteredSegments}
            onManage={handleManage}
            runs={runs}
            navBase={navBase}
            hideManage={hideManage}
          />
        )}

        {!loading && segments.length > 0 && layout === 'grid' && (
          <SegmentGridGrouped
            runs={runs.filter((r) => !r.runId.startsWith('solo-'))}
            onManage={handleManage}
            navBase={navBase}
            hideManage={hideManage}
          />
        )}

        {!loading && hasMoreRuns && (
          <>
            <div ref={loadMoreSentinelRef} aria-hidden style={{ height: 1 }} />
            {loadingMore && (
              <LoadMoreContainer>
                <CircularProgress size={22} sx={{ color: '#28aae1' }} />
              </LoadMoreContainer>
            )}
          </>
        )}
      </CardContent>

      {showCreate && (
        <CreateSegmentModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          projectId={projectId ?? 1}
        />
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Root $compact={useSkinnyView}>
      <Card>{content}</Card>
    </Root>
  );
}

// ─── List View ──────────────────────────────────────────────────────────────

function SegmentTableList({
  segments,
  onManage,
  runs,
  navBase,
  hideManage = false,
}: {
  segments: Segment[];
  onManage: (id: number) => void;
  runs: Run[];
  navBase: string;
  hideManage?: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [visibleCount, setVisibleCount] = useState(25);

  // Build a lookup from segment id → run index for alternating backgrounds
  const segmentToRunIndexMap = useMemo(() => {
    const map = new Map<number, number>();
    runs.forEach((run, runIndex) => {
      run.segments.forEach((seg) => {
        map.set(seg.id, runIndex);
      });
    });
    return map;
  }, [runs]);

  // Mirror the grouped grid's global color index so list and tile views
  // assign the same ring color to the same segment.
  const segmentToColorIndexMap = useMemo(() => {
    const map = new Map<number, number>();
    let idx = 0;
    runs.forEach((run) => {
      run.segments.forEach((seg) => {
        map.set(seg.id, idx++);
      });
    });
    return map;
  }, [runs]);

  // Sort segments by run order, then by creation date within each run
  const sortedSegments = useMemo(() => {
    return [...segments].sort((a, b) => {
      const runIndexA = segmentToRunIndexMap.get(a.id) ?? 999;
      const runIndexB = segmentToRunIndexMap.get(b.id) ?? 999;
      if (runIndexA !== runIndexB) return runIndexA - runIndexB;
      return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
    });
  }, [segments, segmentToRunIndexMap]);

  const visibleSegments = sortedSegments.slice(0, visibleCount);
  const hasMore = visibleCount < sortedSegments.length;

  // Infinite scroll via callback ref — rebinds on HMR remounts.
  const tableObserverRef = useRef<IntersectionObserver | null>(null);
  const sortedLenRef = useRef(sortedSegments.length);
  sortedLenRef.current = sortedSegments.length;
  const tableLoadMoreRef = useCallback((el: HTMLDivElement | null) => {
    tableObserverRef.current?.disconnect();
    tableObserverRef.current = null;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((prev) => Math.min(prev + 25, sortedLenRef.current));
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(el);
    tableObserverRef.current = observer;
  }, []);
  useEffect(() => () => tableObserverRef.current?.disconnect(), []);

  return (
    <>
      <Table>
        <thead>
          <TR $header>
            <TH style={{ flex: 3 }}>Name</TH>
            <TH style={{ flex: 2 }}>Run</TH>
            <TH style={{ flex: 2 }}>Description</TH>
            <TH style={{ flex: 1 }}>Created On</TH>
            {/* <TH style={{ flex: 1 }} /> */}
          </TR>
        </thead>
        <tbody>
          {visibleSegments.map((seg) => {
            const runIndex = segmentToRunIndexMap.get(seg.id) ?? 0;
            const runTitle = runs[runIndex]?.title ?? '—';
            // Use dashboardPageId for navigation if available (for /segmentation/segment routes)
            // Otherwise use v2 id (for /projects/:id/segments routes)
            const targetId = seg.dashboardPageId ?? seg.id;
            return (
              <TR
                key={seg.id}
                onClick={() =>
                  navigate(`${navBase}/${targetId}`, { state: buildBackLinkState(location.pathname) })
                }
                $runIndex={runIndex}
              >
                <TD style={{ flex: 3 }}>
                  <NameCell>
                    <SegmentIconWrap>
                      <RowAvatarRing
                        $color={
                          SEGMENT_COLORS[(segmentToColorIndexMap.get(seg.id) ?? 0) % SEGMENT_COLORS.length]
                        }
                      >
                        {seg.iconUrl ? (
                          <RowAvatarImg src={seg.iconUrl} alt="" />
                        ) : (
                          <People sx={{ fontSize: 16, color: '#adadad' }} />
                        )}
                      </RowAvatarRing>
                    </SegmentIconWrap>
                    <NameLink>{seg.name}</NameLink>
                  </NameCell>
                </TD>
                <TD style={{ flex: 2, color: '#626262', fontSize: 13 }}>{runTitle}</TD>
                <TD style={{ flex: 2 }}>
                  <Description>{seg.description ?? '—'}</Description>
                </TD>
                <TD style={{ flex: 1, color: '#626262', fontSize: 13 }}>
                  {format(new Date(seg.createdOn), 'MM/dd/yyyy')}
                </TD>
                <TD style={{ flex: 1 }}>
                  {!hideManage && (
                    <ManageBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        onManage(seg.id);
                      }}
                    >
                      <Edit sx={{ fontSize: 16 }} />
                      Manage
                    </ManageBtn>
                  )}
                </TD>
              </TR>
            );
          })}
        </tbody>
      </Table>
      {hasMore && <div ref={tableLoadMoreRef} aria-hidden style={{ height: 1 }} />}
    </>
  );
}

// ─── Run Export Dropdown ─────────────────────────────────────────────────────

function RunExportDropdown({ sessionId }: { sessionId: string }): React.JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPpt, setDownloadingPpt] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const anyLoading = downloading || downloadingPpt || downloadingZip;

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const handleExcel = async (): Promise<void> => {
    setMenuOpen(false);
    setDownloading(true);
    try {
      await downloadSegmentedExcel(sessionId);
    } catch (err) {
      console.error('Excel export failed:', err);
      alert(`Export failed: ${(err as Error)?.message ?? 'Unknown error'}`);
    } finally {
      setDownloading(false);
    }
  };

  const handlePpt = async (): Promise<void> => {
    setMenuOpen(false);
    setDownloadingPpt(true);
    try {
      await downloadSegmentedPowerpoint(sessionId);
    } catch (err) {
      console.error('PowerPoint export failed:', err);
      alert(`PPT export failed: ${(err as Error)?.message ?? 'Unknown error'}`);
    } finally {
      setDownloadingPpt(false);
    }
  };

  const handleZip = async (): Promise<void> => {
    setMenuOpen(false);
    setDownloadingZip(true);
    try {
      await downloadSegmentedAllZip(sessionId);
    } catch (err) {
      console.error('Bundle export failed:', err);
      alert(`Bundle export failed: ${(err as Error)?.message ?? 'Unknown error'}`);
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <ExportDropdownWrap ref={wrapRef}>
      <ExportDropdownTrigger
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((o) => !o);
        }}
        disabled={anyLoading}
      >
        <Download size={16} />
        <span>{anyLoading ? 'Downloading…' : 'Download'}</span>
        <ChevronDown size={14} />
      </ExportDropdownTrigger>
      {menuOpen && (
        <ExportDropdownMenu>
          <ExportDropdownItem disabled={downloading} onClick={() => void handleExcel()}>
            <FileText size={16} /> Segment Data (XLS)
          </ExportDropdownItem>
          <ExportDropdownItem disabled={downloadingPpt} onClick={() => void handlePpt()}>
            <Layers size={16} /> Segmentation Report (PPT)
          </ExportDropdownItem>
          <ExportDropdownItem disabled={downloadingZip} onClick={() => void handleZip()}>
            <Archive size={16} /> Download All (ZIP)
          </ExportDropdownItem>
        </ExportDropdownMenu>
      )}
    </ExportDropdownWrap>
  );
}

// ─── Grid View ──────────────────────────────────────────────────────────────

const SEGMENT_COLORS = SEGMENT_CARD_COLORS;

function SegmentGridGrouped({
  runs,
  onManage,
  navBase,
  hideManage = false,
}: {
  runs: Run[];
  onManage: (id: number) => void;
  navBase: string;
  hideManage?: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Track *collapsed* runs (inverse of expanded). Any runId not in this set
  // is expanded, which means newly loaded runs appear expanded on their first
  // render — avoids the collapsed-then-expand flash when infinite scroll
  // appends a page.
  const [collapsedRuns, setCollapsedRuns] = useState<Set<string>>(() => new Set());

  let globalIdx = 0;

  const toggleRun = (runId: string) => {
    setCollapsedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {runs.map((run) => {
        const isExpanded = !collapsedRuns.has(run.runId);
        const dateStr = new Date(run.timestamp).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
        return (
          <RunGroup key={run.runId}>
            <RunGroupHeader>
              <div
                onClick={() => toggleRun(run.runId)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1 }}
              >
                {isExpanded ? <ExpandMore sx={{ fontSize: 18 }} /> : <ChevronRight sx={{ fontSize: 18 }} />}
                <RunGroupTitle>{run.title}</RunGroupTitle>
                <RunGroupMeta>
                  {run.segments.length} segment{run.segments.length !== 1 ? 's' : ''} · {dateStr}
                </RunGroupMeta>
              </div>
              <RunActionsContainer>
                <RunActionButton
                  to={`/segmentation/cross-segment-dashboard?runId=${encodeURIComponent(run.runId)}`}
                  state={buildBackLinkState('Back to Explore Segments', '/explore-segments')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                  <span>Cross-Segment Dashboard</span>
                </RunActionButton>
                <RunActionButton
                  to={
                    run.sessionId
                      ? `/message-generator?segmentationSessionId=${run.sessionId}`
                      : '/message-generator'
                  }
                  onClick={(e) => e.stopPropagation()}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                  <span>Message Generator</span>
                </RunActionButton>
                <RunActionButton
                  to={
                    run.sessionId
                      ? `/message-library?segmentationSessionId=${run.sessionId}`
                      : '/message-library'
                  }
                  onClick={(e) => e.stopPropagation()}
                >
                  <BookOpen size={16} />
                  <span>Message Library</span>
                </RunActionButton>
                {run.sessionId && <RunExportDropdown sessionId={run.sessionId} />}
              </RunActionsContainer>
            </RunGroupHeader>
            {isExpanded && (
              <TileGrid>
                {run.segments.map((seg) => {
                  const i = globalIdx++;
                  const color = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
                  const meta = seg.metadata as {
                    subtitle?: string;
                    sampleData?: { segmentN: number; totalN: number };
                  };
                  // Use dashboardPageId for navigation if available (for /segmentation/segment routes)
                  // Otherwise use v2 id (for /projects/:id/segments routes)
                  const targetId = seg.dashboardPageId ?? seg.id;
                  return (
                    <TileCard
                      key={seg.id}
                      $color={color}
                      onClick={() =>
                        navigate(`${navBase}/${targetId}`, { state: buildBackLinkState(location.pathname) })
                      }
                    >
                      <TileAvatarRing $color={color}>
                        {seg.iconUrl ? (
                          <TileAvatarImg src={seg.iconUrl} alt={seg.name} />
                        ) : (
                          <People sx={{ fontSize: 24, color: '#adadad' }} />
                        )}
                      </TileAvatarRing>
                      <TileInfo>
                        <TileName>{seg.name}</TileName>
                        <TileMeta>
                          {meta.sampleData ? `${meta.sampleData.segmentN.toLocaleString()} HCPs` : ''}
                        </TileMeta>
                      </TileInfo>
                      <TileChevronCircle>
                        <TileChevron />
                      </TileChevronCircle>
                    </TileCard>
                  );
                })}
              </TileGrid>
            )}
          </RunGroup>
        );
      })}
    </div>
  );
}

// ─── Tabbed Grid View (Auto-Generated / Manual) ────────────────────────────

type GridTab = 'auto' | 'manual';

function SegmentGridTabbed({
  segments,
  runs,
  onManage,
  onCreate,
  navBase,
  hideManage = false,
}: {
  segments: Segment[];
  runs: Run[];
  onManage: (id: number) => void;
  onCreate: () => void;
  navBase: string;
  hideManage?: boolean;
}): React.JSX.Element {
  const [tab, setTab] = useState<GridTab>('auto');

  const autoRuns = useMemo(() => runs.filter((r) => !r.runId.startsWith('solo-')), [runs]);

  const manualSegments = useMemo(
    () =>
      segments.filter((s) => {
        const meta = (s.metadata ?? {}) as Record<string, unknown>;
        const sp = (meta.segmentProfile ?? {}) as Record<string, unknown>;
        const runId = (meta._runId ?? sp._runId) as string | undefined;
        return !runId;
      }),
    [segments]
  );

  return (
    <div>
      <SegTabToggle>
        <SegTabBtn $active={tab === 'auto'} onClick={() => setTab('auto')}>
          Auto-Generated ({autoRuns.reduce((s, r) => s + r.segments.length, 0)})
        </SegTabBtn>
        <SegTabBtn $active={tab === 'manual'} onClick={() => setTab('manual')}>
          Manual ({manualSegments.length})
        </SegTabBtn>
      </SegTabToggle>

      {tab === 'auto' &&
        (autoRuns.length > 0 ? (
          <SegmentGridGrouped runs={autoRuns} onManage={onManage} navBase={navBase} hideManage={hideManage} />
        ) : (
          <EmptyState>No auto-generated segments yet. Run a segmentation to create them.</EmptyState>
        ))}

      {tab === 'manual' &&
        (manualSegments.length > 0 ? (
          <ManualGrid
            segments={manualSegments}
            onManage={onManage}
            navBase={navBase}
            hideManage={hideManage}
          />
        ) : (
          <EmptyState>
            No manually created segments.
            <CreateBtnSmall onClick={onCreate}>Create Segment</CreateBtnSmall>
          </EmptyState>
        ))}
    </div>
  );
}

function ManualGrid({
  segments,
  onManage,
  navBase,
  hideManage = false,
}: {
  segments: Segment[];
  onManage: (id: number) => void;
  navBase: string;
  hideManage?: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <TileGrid>
      {segments.map((seg, i) => {
        const color = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
        const meta = seg.metadata as { subtitle?: string };
        // Use dashboardPageId for navigation if available (for /segmentation/segment routes)
        // Otherwise use v2 id (for /projects/:id/segments routes)
        const targetId = seg.dashboardPageId ?? seg.id;
        return (
          <TileCard
            key={seg.id}
            $color={color}
            onClick={() =>
              navigate(`${navBase}/${targetId}`, { state: buildBackLinkState(location.pathname) })
            }
          >
            <TileTop>
              <TileAvatarRing $color={color}>
                {seg.iconUrl ? (
                  <TileAvatarImg src={seg.iconUrl} alt={seg.name} />
                ) : (
                  <People sx={{ fontSize: 24, color: '#adadad' }} />
                )}
              </TileAvatarRing>
              <TileInfo>
                <TileName>{seg.name}</TileName>
                <TileMeta>{meta.subtitle ?? seg.description ?? ''}</TileMeta>
              </TileInfo>
            </TileTop>
            {/* <TileFooter>
              <TileChevronCircle>
                <TileChevron />
              </TileChevronCircle>
            </TileFooter> */}
          </TileCard>
        );
      })}
    </TileGrid>
  );
}

const SegTabToggle = styled.div`
  display: flex;
  background: #f2f2f2;
  border-radius: 8px;
  padding: 3px;
`;

const SegTabBtn = styled.button<{ $active: boolean }>`
  padding: 5px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  background: ${(p) => (p.$active ? '#ffffff' : 'transparent')};
  color: ${(p) => (p.$active ? '#323232' : '#626262')};
  box-shadow: ${(p) => (p.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none')};
  &:hover {
    color: #323232;
  }
`;

// ─── Create Modal ───────────────────────────────────────────────────────────

function CreateSegmentModal({
  onClose,
  onCreated,
  projectId,
}: {
  onClose: () => void;
  onCreated: (seg: Segment) => void;
  projectId: number;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async (): Promise<void> => {
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await client().post('/segments', {
        projectId,
        name: name.trim(),
        description: description.trim() || undefined,
        metadata: { subtitle: subtitle.trim() || undefined },
      });
      onCreated(res.data.segment);
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to create segment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>Create New Segment</ModalHeader>
        <ModalSubtitle>Create a new segment and tag</ModalSubtitle>
        <ModalBody>
          <Field>
            <Label>Segment Title</Label>
            <Input
              type="text"
              placeholder="Segment A"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </Field>
          <Field>
            <Label>Subtitle</Label>
            <Input
              type="text"
              placeholder="e.g. Top 20% by volume"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Description</Label>
            <Textarea
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </Field>
        </ModalBody>
        {error && <ModalError>{error}</ModalError>}
        <ModalFooter>
          <CancelBtn onClick={onClose} disabled={saving}>
            Cancel
          </CancelBtn>
          <PrimaryBtn onClick={handleCreate} disabled={saving || !name.trim()}>
            {saving ? 'Creating...' : 'Create'}
          </PrimaryBtn>
        </ModalFooter>
      </ModalCard>
    </ModalOverlay>
  );
}

// ─── Styled — Card Shell (matches frontend Card/GroupCardHeader) ────────────

const Root = styled.div<{ $compact?: boolean }>`
  min-height: calc(100vh - 60px);
  background: transparent;
  padding: ${(p) => (p.$compact ? '4px 24px 96px' : '24px 24px 96px')};
`;

const Card = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  background: transparent;
  border: none;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 60px - 48px);
`;

const CardHeader = styled.div<{ $compact?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(p) => (p.$compact ? '12px 20px' : '20px 20px')};
  background: #ffffff;
  border: 1px solid var(--sec-01, #e5e5e5);
  border-radius: 12px;
  margin-bottom: ${(p) => (p.$compact ? '12px' : '16px')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;
const HeaderTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const HeaderBackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #626262;
  font-size: 12px;
  padding: 0;
  cursor: pointer;
  align-self: flex-start;
  &:hover {
    color: #1271c4;
  }
`;
const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #323232;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LayoutToggle = styled.div`
  display: flex;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  overflow: hidden;
`;
const LayoutBtn = styled.button<{ $active: boolean }>`
  padding: 6px 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  background: ${(p) => (p.$active ? '#e5e5e5' : '#fff')};
  color: ${(p) => (p.$active ? '#323232' : '#adadad')};
  &:hover {
    background: #f2f2f2;
  }
`;

const MigrateBtn = styled.button`
  height: 40px;
  padding: 8px 16px;
  border-radius: 5px;
  border: 1px solid var(--pri-02, #28aae1);
  background: transparent;
  color: var(--pri-02, #28aae1);
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) {
    background: rgba(40, 170, 225, 0.04);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CreateBtn = styled.button`
  height: 40px;
  padding: 8px 16px;
  border-radius: 5px;
  border: none;
  background: var(--pri-02, #28aae1);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }
`;

const CreateBtnSmall = styled(CreateBtn)`
  height: 32px;
  padding: 4px 14px;
  font-size: 13px;
  margin-top: 8px;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--sec-01, #e5e5e5);
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 64px;
  color: #626262;
  font-size: 14px;
  text-align: center;
  gap: 8px;
`;

// ─── Styled — Table List View ───────────────────────────────────────────────

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TR = styled.tr<{ $header?: boolean; $runIndex?: number }>`
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--gray-l, #e5e5e5);
  ${(p) => p.$header && `background: var(--gray-xl, #fafafa); border-top: 1px solid var(--gray-l, #e5e5e5);`}
  ${(p) =>
    !p.$header &&
    `cursor: pointer;
     background: ${p.$runIndex != null && p.$runIndex % 2 === 0 ? '#fafafa' : '#ffffff'};
     &:hover { background: ${p.$runIndex != null && p.$runIndex % 2 === 0 ? '#f0f0f0' : '#f5f5f5'}; }`}
`;

const TH = styled.th`
  padding: 10px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  color: #323232;
`;

const TD = styled.td`
  padding: 6px 16px;
  font-size: 14px;
  color: #323232;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
`;
const SegmentIconWrap = styled.div`
  margin-right: 10px;
  flex-shrink: 0;
  display: flex;
`;
const RowAvatarRing = styled.div<{ $color?: string }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid ${(p) => p.$color ?? '#28aae1'};
  padding: 2px;
  background: white;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const RowAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
`;
const NameLink = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  &:hover {
    color: var(--hyperlink, #28aae1);
    text-decoration: underline;
  }
`;

const Description = styled.p`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  color: #626262;
  font-size: 13px;
`;

const ManageBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  cursor: pointer;
  transition:
    background 0.2s,
    border-color 0.2s;
  margin-left: auto;
  color: var(--gray-d, #626262);
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  &:hover {
    background: var(--gray-xl, #fafafa);
    border-color: rgba(0, 0, 0, 0.2);
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

// ─── Styled — Run Groups ────────────────────────────────────────────────────

const RunGroup = styled.div`
  background: #ffffff;
  border: 1px solid var(--gray-l, #e5e5e5);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const RunGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 20px;
`;

const RunGroupTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--black, #323232);
`;

const RunGroupMeta = styled.div`
  font-size: 12px;
  color: var(--gray-d, #626262);
`;

const RunActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const RunActionButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #323232;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  text-decoration: none;
  transition:
    border-color 0.2s,
    background-color 0.2s,
    transform 0.1s;

  &:hover {
    border-color: #28aae1;
    background-color: #f8fcff;
    transform: translateY(-1px);
    color: #323232;
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    color: #565add;
  }
`;

// ─── Styled — Run Export Dropdown ───────────────────────────────────────────

const ExportDropdownWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const ExportDropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #323232;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition:
    border-color 0.2s,
    background-color 0.2s,
    transform 0.1s;

  &:hover:not(:disabled) {
    border-color: #28aae1;
    background-color: #f8fcff;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    color: #565add;
  }
`;

const ExportDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  overflow: hidden;
  z-index: 20;
`;

const ExportDropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #ffffff;
  border: none;
  border-bottom: 1px solid #f2f2f2;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #323232;
  font-family: inherit;
  text-align: left;
  transition: background 0.15s;
  &:last-child {
    border-bottom: none;
  }
  &:hover:not(:disabled) {
    background: #f5fbfd;
    color: #1d94c7;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Styled — Tile Grid View (matches ExploreSegments cards) ────────────────

const TileGrid = styled.div`
  padding: 8px 12px;
  border-top: 1px solid var(--gray-l, #e5e5e5);
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const TileChevron = styled(ChevronRight)`
  color: #9a9a9a;
  font-size: 18px;
  transition:
    transform 0.2s ease,
    color 0.2s ease;
`;

const TileChevronCircle = styled.div`
  border-radius: 50%;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  flex-shrink: 0;
`;

const TileCard = styled.div<{ $color: string }>`
  background: #fff;
  border: 2px solid #e5e5e5;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px 6px 8px;
  &:hover {
    background-color: #e5f5fc;
    border-color: ${(p) => p.$color};
  }
  &:hover ${TileChevron} {
    transform: translateX(2px);
    color: ${(p) => p.$color};
  }
`;

const TileTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TileAvatarRing = styled.div<{ $color?: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid ${(p) => p.$color ?? '#28aae1'};
  padding: 2px;
  background: white;
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const TileAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
`;

const TileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;
const TileName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
`;
const TileMeta = styled.div`
  font-size: 11px;
  color: #626262;
`;

// ─── Styled — Modal (matches frontend CreateSegment.Modal) ──────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
`;
const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  width: 520px;
  max-width: 90vw;
`;
const ModalHeader = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #323232;
  padding: 20px 20px 0;
`;
const ModalSubtitle = styled.div`
  font-size: 13px;
  color: #626262;
  padding: 4px 20px 16px;
`;
const ModalBody = styled.div`
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ModalError = styled.div`
  margin: 12px 20px 0;
  font-size: 13px;
  color: #e12e40;
  background: #fde5e5;
  border: 1px solid #e12e40;
  border-radius: 4px;
  padding: 8px 12px;
`;
const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 20px;
  border-top: 1px solid #e5e5e5;
  margin-top: 16px;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #323232;
`;
const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  color: #323232;
  outline: none;
  &::placeholder {
    color: #adadad;
  }
  &:focus {
    border-color: var(--pri-02, #28aae1);
  }
`;
const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  color: #323232;
  outline: none;
  resize: vertical;
  &::placeholder {
    color: #adadad;
  }
  &:focus {
    border-color: var(--pri-02, #28aae1);
  }
`;
const CancelBtn = styled.button`
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid #d3d3d3;
  background: #fff;
  color: #323232;
  &:hover {
    background: #f2f2f2;
  }
  &:disabled {
    opacity: 0.5;
  }
`;
const PrimaryBtn = styled.button`
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: var(--pri-02, #28aae1);
  color: #fff;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
  }
`;
